import type {
  Brand,
  BrandStyles,
  Campaign,
  FieldId,
  LayoutBlock,
  LayoutBorder,
  LayoutCol,
  LayoutRow,
  Padding,
  Person,
  RenderInput,
  RenderedMap,
  RenderOptions,
  RenderResult,
  RoleRef,
  SignatureLayout,
} from './types.ts';
import { BANNER_DISPLAY_WIDTH, bannerDisplayHeight, GMAIL_SIGNATURE_MAX_CHARS } from './types.ts';
import { resolveBrand } from './brand.ts';
import { hasAnyCampaign, pickCampaign } from './campaign.ts';
import { effectiveLayout } from './layout.ts';
import { bannerPath, gmailClickPath, localClickPath } from './routes.ts';
import { SOCIAL_NETWORKS, socialIconUrl, socialLinks } from './social.ts';
import { blockRole, cssWeight, effectiveStyles, textStyleOf } from './styles.ts';
import { expandTemplate, type TemplateValues } from './template.ts';
import { esc, safeColor, safeFont, safeUrl, shortHash, telHref, withQuery } from './util.ts';

/*
 * Moteur de rendu des signatures — UN SEUL moteur (docs/EDITEUR-MISE-EN-PAGE.md §0) : le canevas de
 * l'éditeur et la signature poussée dans Gmail sortent d'ici, à partir de l'arbre de mise en page
 * (layout_tree, ou sa construction à la volée depuis les anciens champs de la charte).
 *
 * Règles « HTML d'email » (§2), sans exception :
 * - tout tableau porte role="presentation" border="0" cellpadding="0" cellspacing="0" ;
 * - les largeurs sont posées en attribut HTML ET en style inline ;
 * - ni flex, ni grid, ni float, ni margin : les espacements sont des padding sur les <td> ;
 * - les hauteurs de ligne explicites portent mso-line-height-rule:exactly ;
 * - images : width/height en attributs, display:block, border="0", alt renseigné ;
 * - toute donnée est échappée (esc), toute URL filtrée (safeUrl), toute couleur validée (safeColor).
 *
 * Effondrement des vides : un bloc sans donnée ne produit rien ; une colonne sans bloc produit est
 * omise et sa largeur redistribuée aux colonnes voisines ; une ligne sans colonne est omise.
 */

const DEFAULT_FONT = 'Arial, Helvetica, sans-serif';
const TABLE = 'role="presentation" border="0" cellpadding="0" cellspacing="0"';

interface Ctx {
  person: Person;
  sendAs: string;
  brand: Brand;
  campaign: Campaign | null;
  /** toutes les campagnes actives (mode local : décide de l'emplacement de bannière) */
  campaigns: Campaign[];
  opts: RenderOptions;
  warnings: string[];
  layoutWidth: number; // gabarit, pour la cohérence des largeurs de colonnes
  rowIndex: Map<string, number>; // numéro (1…) des lignes de premier niveau, pour les avertissements
  /** Styles par rôle de la charte (docs/REFONTE-CHARTES.md) : la seule source de couleur, taille, graisse. */
  st: BrandStyles;
  font: string;
  base: string;
}

// ---------------------------------------------------------------- valeurs des champs

function personName(p: Person, brand: Brand): string {
  if (p.display_name) return p.display_name;
  const full = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  if (full) return full;
  return p.kind === 'shared' ? brand.name : p.primary_email;
}

const hostOf = (site: string) => site.replace(/^https?:\/\//i, '').replace(/\/$/, '');

/** Texte brut d'un champ pour cette personne ; '' = le bloc ne produit rien. */
export function fieldValue(brand: Brand, person: Person, sendAs: string, field: FieldId): string {
  const shared = person.kind === 'shared';
  switch (field) {
    case 'name':
      return personName(person, brand);
    case 'title':
      return shared ? person.job_title || 'Nous contacter' : person.job_title || '';
    case 'phone_mobile':
      return person.phone_mobile || '';
    case 'phone_office':
      return person.phone_office || (shared ? brand.phone || '' : '');
    case 'email':
      return sendAs;
    case 'address':
      return brand.address || '';
    case 'website': {
      const site = safeUrl(brand.website_url);
      return site ? hostOf(site) : '';
    }
    case 'legal':
      return brand.legal_mention || '';
  }
}

function templateValues(ctx: Ctx): TemplateValues {
  const { brand: b, person: p, sendAs } = ctx;
  const v = (f: FieldId) => fieldValue(b, p, sendAs, f);
  return {
    prenom: p.first_name,
    nom: p.last_name,
    fonction: v('title'),
    lieu: p.kind === 'shared' ? '' : b.name, // variable {{lieu}} des blocs texte libres
    email: sendAs,
    tel_mobile: v('phone_mobile'),
    tel_fixe: v('phone_office'),
    adresse: v('address'),
    site: v('website'),
  };
}

// ---------------------------------------------------------------- blocs

/** URL des images de la signature (logo, bannière, icônes) : pour estimer son poids à l'ouverture d'un mail. */
export function signatureImageUrls(html: string): string[] {
  return [...new Set([...html.matchAll(/<img [^>]*src="([^"]+)"/g)].map((m) => m[1].replace(/&amp;/g, '&')))];
}

/** Poids estimé : HTML en octets UTF-8 + images connues ; `unknown` = images dont le poids n'a pas pu être lu. */
export function estimateWeight(html: string, imageBytes: Record<string, number | null>): { bytes: number; unknown: number } {
  let bytes = new TextEncoder().encode(html).length;
  let unknown = 0;
  for (const url of signatureImageUrls(html)) {
    const n = imageBytes[url];
    if (typeof n === 'number') bytes += n;
    else unknown++;
  }
  return { bytes, unknown };
}

/** Seuil au-delà duquel le poids d'une signature pèse sur la bande passante à chaque ouverture (Signitic : orange). */
export const WEIGHT_WARN_BYTES = 500_000;

const pad = (p: Padding | undefined): string => (p ? `padding:${p.map((n) => `${Math.max(0, Math.round(n))}px`).join(' ')}` : '');
/** Trait de colonne : épaisseur du trait, couleur du rôle `separator`. */
const borderCss = (side: string, b: LayoutBorder | undefined, color: string): string =>
  b ? `border-${side}:${Math.max(0, Math.round(b.width))}px solid ${color}` : '';

/**
 * Typographie COMPLÈTE d'un rôle texte, en ligne : police, taille (px), interligne (px), graisse,
 * couleur. Posée sur chaque cellule ET sur chaque lien du texte : rien n'est laissé à l'héritage du
 * <td> ou du <table>, car Apple Mail réapplique sa police par défaut (Helvetica 12 pt, interligne
 * lâche) à tout élément collé qui ne déclare pas la sienne (constaté le 23/09/2026 sur le bloc
 * contact). Interligne par défaut : 1,4 × la taille, arrondi, avec mso-line-height-rule pour Outlook.
 */
function roleTypography(ctx: Ctx, role: RoleRef): string {
  const r = textStyleOf(ctx.st, role);
  const size = Math.round(r.size);
  const lh = r.lineHeight ? Math.round(r.lineHeight) : Math.round(size * 1.4);
  const parts = [
    `font-family:${ctx.font}`, // depuis le 30/09 le Nom aussi : Arial Black (police d'affiche) se referme à 14–17 px
    `font-size:${size}px`,
    `line-height:${lh}px`,
    'mso-line-height-rule:exactly',
    `font-weight:${cssWeight(r.weight)}`,
    ...(r.italic ? ['font-style:italic'] : []),
    `color:${r.color}`,
  ];
  // Interlettrage : toujours en px (jamais em/rem, mal rendus par Outlook Windows), au dixième.
  if (r.letterSpacing) parts.push(`letter-spacing:${Math.round(r.letterSpacing * 10) / 10}px`);
  return parts.join(';');
}

/** Style d'un lien dans un bloc texte : la typographie du rôle, sans soulignement. */
const linkStyle = (ctx: Ctx, role: RoleRef) => `${roleTypography(ctx, role)};text-decoration:none`;

/** Styles inline de la cellule d'un bloc : marges du bloc, puis la typographie complète de son rôle. */
function blockCellStyle(ctx: Ctx, block: LayoutBlock, effectivePadding: Padding | undefined): string {
  const parts: string[] = [];
  const padding = pad(effectivePadding);
  if (padding) parts.push(padding);
  const role = blockRole(block);
  if (role && role !== 'social' && role !== 'separator') parts.push(roleTypography(ctx, role));
  return parts.join(';');
}

function linked(html: string, href: string | undefined, extraStyle = ''): string {
  const url = safeUrl(href);
  return url ? `<a href="${esc(url)}" target="_blank" style="text-decoration:none${extraStyle}">${html}</a>` : html;
}

function logoHtml(ctx: Ctx, block: LayoutBlock): string {
  const b = ctx.brand;
  const logo = safeUrl(b.logo_url);
  if (logo) {
    const w = Math.max(1, Math.round(b.logo_width || 145));
    const h = b.logo_height ? ` height="${Math.round(b.logo_height)}"` : '';
    const hs = b.logo_height ? `height:${Math.round(b.logo_height)}px;` : '';
    const img = `<img src="${esc(logo)}" alt="${esc(b.name)}" width="${w}"${h} style="display:block;border:0;width:${w}px;${hs}">`;
    return linked(img, block.href ?? b.website_url ?? undefined);
  }
  ctx.warnings.push(`Lieu « ${b.name} » : pas de logo, un logotype texte est utilisé.`);
  return `<span style="font-family:${ctx.font};font-size:16px;line-height:22px;mso-line-height-rule:exactly;font-weight:bold;color:${ctx.st.name.color}">${esc(b.name)}</span>`;
}

function fieldHtml(ctx: Ctx, block: LayoutBlock & { type: 'field' }): string {
  const value = fieldValue(ctx.brand, ctx.person, ctx.sendAs, block.field);
  if (!value) return '';
  // Retours à la ligne (plusieurs fonctions sur une personne, adresse sur deux lignes) : un <br> par
  // ligne, dans la même cellule ; l'interligne en px du rôle (mso-line-height-rule:exactly) s'applique
  // à chaque ligne. Jamais de <p>/<div>. Les champs liens (e-mail, téléphones) n'ont pas de retour.
  const text = esc(value).replace(/\n/g, '<br>');
  const role = (blockRole(block) ?? 'body') as RoleRef;
  const link = linkStyle(ctx, role);
  if (block.href) return linked(text, block.href, `;${roleTypography(ctx, role)}`);
  switch (block.field) {
    case 'email':
      return `<a href="mailto:${esc(value)}" style="${link}">${text}</a>`;
    case 'phone_mobile':
    case 'phone_office':
      return `<a href="${esc(telHref(value))}" style="${link}">${text}</a>`;
    case 'website': {
      const site = safeUrl(ctx.brand.website_url);
      return site ? `<a href="${esc(site)}" target="_blank" style="${link}">${text}</a>` : text;
    }
    default:
      return text;
  }
}

/** Icônes des réseaux : une cellule par réseau, espacement par padding sur le <td>, <a> autour de l'<img>, alt lisible. */
function socialHtml(ctx: Ctx, block: LayoutBlock & { type: 'social' }): string {
  const b = ctx.brand;
  const links = socialLinks(b, block.networks)
    .map(({ network, url }) => ({ network, url: safeUrl(url) }))
    .filter((l): l is { network: (typeof SOCIAL_NETWORKS)[number]; url: string } => l.url !== null);
  if (links.length === 0) return '';
  const s = Math.max(8, Math.round(ctx.st.social.size));
  const gap = 6;
  const cells = links.map(({ network, url }, i) =>
    `<td style="padding:0 ${i < links.length - 1 ? gap : 0}px 0 0"><a href="${esc(url)}" target="_blank" style="text-decoration:none"><img src="${
      esc(socialIconUrl(ctx.base, b, network.id, ctx.st.social.color))
    }" alt="${network.label}" width="${s}" height="${s}" style="display:block;border:0;width:${s}px;height:${s}px"></a></td>`
  );
  return `<table ${TABLE} style="border-collapse:collapse"><tr>${cells.join('')}</tr></table>`;
}

/**
 * Bannière : figée et versionnée en mode Gmail (dimensions réelles du visuel, affiché à 600 px de
 * large, hauteur proportionnelle) ; dynamique en mode local (URL fixe résolue côté serveur à chaque
 * affichage : la hauteur n'est pas connue au rendu, seule la largeur est posée et le client mail
 * garde la proportion). Plus de bannière « par défaut » de charte : une bannière permanente est une
 * campagne sans dates (phase 2, 22/09/2026).
 */
function bannerHtml(ctx: Ctx): string {
  const { campaign: c, brand: b, person: p, opts, sendAs, base } = ctx;
  // Boîte sensible : aucune bannière, dans les deux modes.
  if (p.no_banner) return '';
  const w = BANNER_DISPLAY_WIDTH;

  if (opts.mode === 'local') {
    if (!hasAnyCampaign(ctx.campaigns, { brandId: b.id, emails: [p.primary_email, sendAs], noBanner: p.no_banner })) {
      ctx.warnings.push(`Mode local : aucune campagne active ne cible cette adresse, la signature est générée sans emplacement bannière.`);
      return '';
    }
    // public_token (et non install_token) : ces URL sont lisibles par tous les destinataires.
    const src = bannerPath(base, p.public_token, sendAs);
    const href = localClickPath(base, p.public_token, sendAs);
    const tag = `<img src="${esc(src)}" alt="${esc(b.name)}" width="${w}" style="display:block;border:0;width:${w}px;max-width:100%;height:auto">`;
    return `<a href="${esc(href)}" target="_blank">${tag}</a>`;
  }

  if (!c) return '';
  const src = safeUrl(c.image_url);
  if (!src) {
    ctx.warnings.push(`Campagne « ${c.name} » : URL d'image invalide.`);
    return '';
  }
  const h = bannerDisplayHeight(c.image_width, c.image_height);
  const versioned = withQuery(src, { v: shortHash(c.id + c.updated_at) });
  const href = c.link_url ? gmailClickPath(base, c.id, p.id) : null;
  const tag = `<img src="${esc(versioned)}" alt="${esc(c.alt || c.name)}" width="${w}" height="${h}" style="display:block;border:0;width:${w}px;max-width:100%;height:auto">`;
  return href ? `<a href="${esc(href)}" target="_blank">${tag}</a>` : tag;
}

function textHtml(ctx: Ctx, block: LayoutBlock & { type: 'text' }): string {
  const expanded = expandTemplate(block.text, templateValues(ctx));
  if (!expanded) return '';
  const html = esc(expanded).replace(/\n/g, '<br>');
  return block.href ? linked(html, block.href, `;${roleTypography(ctx, block.role ?? 'body')}`) : html;
}

function imageHtml(block: LayoutBlock & { type: 'image' }): string {
  const url = safeUrl(block.url);
  if (!url) return '';
  const w = Math.max(1, Math.round(block.width));
  const h = Math.max(1, Math.round(block.height));
  const img = `<img src="${esc(url)}" alt="" width="${w}" height="${h}" style="display:block;border:0;width:${w}px;height:${h}px">`;
  return linked(img, block.href);
}

/** Contenu d'un bloc : '' si le bloc n'a rien à montrer pour cette personne. Espace et filet ont toujours un contenu. */
function blockContent(ctx: Ctx, block: LayoutBlock): string {
  if (block.type === 'spacer') return '&nbsp;';
  if (block.type === 'divider') return '&nbsp;';
  let content = '';
  switch (block.type) {
    case 'logo':
      content = logoHtml(ctx, block);
      break;
    case 'field':
      content = fieldHtml(ctx, block);
      break;
    case 'social':
      content = socialHtml(ctx, block);
      break;
    case 'banner':
      content = block.href ? linked(bannerHtml(ctx), block.href) : bannerHtml(ctx);
      break;
    case 'text':
      content = textHtml(ctx, block);
      break;
    case 'image':
      content = imageHtml(block);
      break;
  }
  return content;
}

/** Cellule d'un bloc dont le contenu est connu, avec ses marges effectives. */
function blockCell(ctx: Ctx, block: LayoutBlock, content: string, padding: Padding | undefined): string {
  if (block.type === 'spacer') {
    const h = Math.max(1, Math.round(block.height));
    return `<td height="${h}" style="height:${h}px;line-height:${h}px;font-size:1px;mso-line-height-rule:exactly">&nbsp;</td>`;
  }
  if (block.type === 'divider') {
    const w = Math.max(1, Math.round(block.width ?? 1));
    return `<td style="${[pad(padding), `border-top:${w}px solid ${ctx.st.separator.color}`, 'font-size:1px', 'line-height:1px'].filter(Boolean).join(';')}">&nbsp;</td>`;
  }
  const style = blockCellStyle(ctx, block, padding);
  return `<td${style ? ` style="${style}"` : ''}>${content}</td>`;
}

/**
 * Marges effectives des blocs d'une colonne : un bloc sans contenu pour cette personne (fonction
 * vide, téléphone absent…) disparaît, mais l'espacement qu'il portait est REPORTÉ sur ses voisins —
 * sa marge basse sur le dernier bloc affiché avant lui, sa marge haute sur le premier bloc affiché
 * après lui (au maximum, jamais cumulé). Ainsi la respiration entre l'en-tête (nom, fonction) et
 * les contacts survit à l'absence de la fonction : le nom ne colle pas à l'e-mail.
 */
function effectivePaddings(blocks: LayoutBlock[], contents: string[]): (Padding | undefined)[] {
  const eff: (Padding | undefined)[] = blocks.map((b) => b.padding);
  let lastShown = -1;
  let carryTop = 0;
  blocks.forEach((b, i) => {
    if (contents[i]) {
      if (carryTop) eff[i] = withSide(eff[i], 0, carryTop);
      carryTop = 0;
      lastShown = i;
      return;
    }
    const p = b.padding;
    if (!p) return;
    carryTop = Math.max(carryTop, p[0]);
    if (lastShown >= 0 && p[2] > 0) eff[lastShown] = withSide(eff[lastShown], 2, p[2]);
  });
  return eff;
}
const withSide = (p: Padding | undefined, side: 0 | 1 | 2 | 3, value: number): Padding => {
  const next: Padding = p ? [...p] as Padding : [0, 0, 0, 0];
  next[side] = Math.max(next[side], value);
  return next;
};

// ---------------------------------------------------------------- colonnes et lignes

interface ColOut {
  col: LayoutCol;
  html: string; // contenu de la cellule ('' si vide)
  blocks: string[]; // identifiants des blocs réellement émis, dans l'ordre
}

function colContent(ctx: Ctx, col: LayoutCol, depth: number): { html: string; blocks: string[] } {
  const contents = col.blocks.map((b) => blockContent(ctx, b));
  const paddings = effectivePaddings(col.blocks, contents);
  const cells = col.blocks.map((b, i) => ({ id: b.id, html: contents[i] ? blockCell(ctx, b, contents[i], paddings[i]) : '' })).filter((c) => c.html);
  const blocks = cells.length
    ? `<table ${TABLE} width="100%" style="width:100%;border-collapse:collapse">${cells.map((c) => `<tr>${c.html}</tr>`).join('')}</table>`
    : '';
  const rows = (col.rows ?? []).map((r) => rowHtml(ctx, r, depth + 1).html).filter(Boolean).join('');
  return { html: blocks + rows, blocks: cells.map((c) => c.id) };
}

/**
 * Largeur minimale du CONTENU d'une colonne : marges gauche + droite, plus le plus large des blocs à
 * largeur fixe (logo, image, icônes réseaux, bannière) et des lignes imbriquées. Un `<td>` déclaré plus
 * étroit que cela est élargi à l'émission : WebKit (Apple Mail, Gmail) élargirait de lui-même, le moteur
 * Word d'Outlook Windows respecte la largeur déclarée et casserait la mise en page. `emitted` limite le
 * calcul aux blocs réellement rendus pour cette personne (sans, tous les blocs comptent : usage éditeur).
 */
export function columnMinWidth(brand: Brand, styles: BrandStyles, col: LayoutCol, emitted?: ReadonlySet<string>): number {
  const p = col.padding ?? [0, 0, 0, 0];
  let inner = 0;
  for (const b of col.blocks) {
    if (emitted && !emitted.has(b.id)) continue;
    let w = 0;
    if (b.type === 'logo') w = safeUrl(brand.logo_url) ? Math.max(1, Math.round(brand.logo_width || 145)) : 0;
    else if (b.type === 'image') w = Math.max(1, Math.round(b.width));
    else if (b.type === 'banner') w = BANNER_DISPLAY_WIDTH;
    else if (b.type === 'social') {
      const n = socialLinks(brand, b.networks).filter((l) => safeUrl(l.url)).length;
      const s = Math.max(8, Math.round(styles.social.size));
      w = n ? n * s + (n - 1) * 6 : 0;
    }
    inner = Math.max(inner, w);
  }
  for (const r of col.rows ?? []) {
    inner = Math.max(inner, r.cols.reduce((sum, c) => sum + (typeof c.width === 'number' ? Math.max(c.width, columnMinWidth(brand, styles, c, emitted)) : columnMinWidth(brand, styles, c, emitted)), 0));
  }
  return inner ? inner + Math.max(0, p[3]) + Math.max(0, p[1]) : 0;
}

/**
 * Colonne « média » : elle ne contient que des blocs à largeur fixe (logo, image), éventuellement un
 * espace ou un filet. Sa largeur n'est pas un réglage : c'est celle de son contenu (largeur du logo
 * réglée dans la fiche, ou de l'image) plus ses marges gauche et droite. null = pas une colonne média.
 * C'est la décision du 08/10/2026 (option a) : plus aucun moyen de saisir une largeur fausse.
 */
export function mediaColumnWidth(brand: Brand, styles: BrandStyles, col: LayoutCol): number | null {
  if (!col.blocks.length || (col.rows ?? []).length) return null;
  const media = col.blocks.filter((b) => b.type === 'logo' || b.type === 'image');
  if (!media.length || col.blocks.some((b) => !['logo', 'image', 'spacer', 'divider'].includes(b.type))) return null;
  const w = columnMinWidth(brand, styles, col);
  return w > 0 ? w : null;
}

export interface WidthCorrection {
  rowIndex: number; // 1…
  colIndex: number; // 1…
  colId: string;
  /** 'raise' : largeur déclarée inférieure au contenu, relevée (le défaut réel) ; 'gutter' : largeur supérieure au contenu, l'excédent devient une marge explicite (gouttière), largeur inchangée. */
  kind: 'raise' | 'gutter';
  from: number | string;
  to: number;
  /** Pour 'gutter' : la marge (droite, ou gauche si la colonne est alignée à droite) après correction. */
  gutter?: number;
}

/**
 * L'arbre avec chaque colonne média cohérente, et la liste de ce qui a changé (vide = déjà cohérent).
 * Ne RÉDUIT jamais une largeur (08/10) : un excédent sur le contenu est un espacement voulu entre le
 * logo et le texte (la gouttière, +16 px sur la-casbah, panic-room, virage) ; il est rendu explicite en
 * marge de colonne, modifiable dans l'éditeur, à largeur totale strictement identique.
 */
export function fitLayoutWidths(brand: Brand, styles: BrandStyles, layout: SignatureLayout): { layout: SignatureLayout; corrections: WidthCorrection[] } {
  const corrections: WidthCorrection[] = [];
  const rows = layout.rows.map((r, ri) => ({
    ...r,
    cols: r.cols.map((c, ci) => {
      const want = mediaColumnWidth(brand, styles, c);
      if (want === null || typeof c.width !== 'number' || c.width === want) return c;
      if (c.width < want) {
        corrections.push({ rowIndex: ri + 1, colIndex: ci + 1, colId: c.id, kind: 'raise', from: c.width, to: want });
        return { ...c, width: want };
      }
      const excess = Math.round(c.width - want);
      const p: Padding = c.padding ? [...c.padding] as Padding : [0, 0, 0, 0];
      const side = c.align === 'right' ? 3 : 1; // gouttière côté texte : à droite d'un logo à gauche, à gauche d'un logo à droite
      p[side] += excess;
      corrections.push({ rowIndex: ri + 1, colIndex: ci + 1, colId: c.id, kind: 'gutter', from: c.width, to: c.width, gutter: p[side] });
      return { ...c, padding: p };
    }),
  }));
  return { layout: corrections.length ? { ...layout, rows } : layout, corrections };
}

const widthAttr = (w: number | string): { attr: string; css: string } => {
  if (typeof w === 'number') return { attr: ` width="${Math.round(w)}"`, css: `width:${Math.round(w)}px;` };
  if (/^\d+(\.\d+)?%$/.test(w)) return { attr: ` width="${w}"`, css: `width:${w};` };
  return { attr: '', css: '' };
};

function colCell(ctx: Ctx, out: ColOut, width: number | string): string {
  const c = out.col;
  const w = widthAttr(width);
  const style = [
    w.css.replace(/;$/, ''),
    `vertical-align:${c.valign ?? 'top'}`,
    pad(c.padding),
    borderCss('left', c.borderLeft, ctx.st.separator.color),
    borderCss('right', c.borderRight, ctx.st.separator.color),
    borderCss('top', c.borderTop, ctx.st.separator.color),
    borderCss('bottom', c.borderBottom, ctx.st.separator.color),
    c.backgroundColor ? `background-color:${safeColor(c.backgroundColor, '#ffffff')}` : '',
    c.align ? `text-align:${c.align}` : '',
  ].filter(Boolean).join(';');
  return `<td${w.attr} valign="${c.valign ?? 'top'}" style="${style}">${out.html}</td>`;
}

/**
 * Une ligne : colonnes vides omises, leur largeur en px redistribuée aux voisines (une colonne
 * 'auto' l'absorbe d'elle-même ; sinon partage égal entre les colonnes en px). '' si tout est vide.
 */
function rowHtml(ctx: Ctx, row: LayoutRow, depth: number): { html: string; cols: RenderedMap['rows'][number]['cols'] } {
  const outs: ColOut[] = row.cols.map((col) => ({ col, ...colContent(ctx, col, depth) }));
  const kept = outs.filter((o) => o.html);
  if (kept.length === 0) return { html: '', cols: [] };
  const dropped = outs.filter((o) => !o.html);
  const lostPx = dropped.reduce((n, o) => n + (typeof o.col.width === 'number' ? o.col.width : 0), 0);
  const widths: (number | string)[] = kept.map((o) => o.col.width);
  // Largeur déclarée jamais inférieure au contenu (cf. columnMinWidth) ; on le dit dans les avertissements.
  const rowLabel = `Ligne ${ctx.rowIndex.get(row.id) ?? '?'}`;
  kept.forEach((o, i) => {
    const w = widths[i];
    if (typeof w !== 'number') return;
    const min = columnMinWidth(ctx.brand, ctx.st, o.col, new Set(o.blocks));
    if (min > w) {
      widths[i] = min;
      ctx.warnings.push(`${rowLabel}, colonne ${row.cols.indexOf(o.col) + 1} : largeur déclarée ${Math.round(w)} px inférieure à son contenu (${min} px), élargie à ${min} px.`);
    }
  });
  if (lostPx > 0 && !widths.some((w) => w === 'auto')) {
    const pxIdx = widths.map((w, i) => (typeof w === 'number' ? i : -1)).filter((i) => i >= 0);
    if (pxIdx.length) {
      const share = lostPx / pxIdx.length;
      for (const i of pxIdx) widths[i] = (widths[i] as number) + share;
    }
  }
  if (depth === 1) {
    const fixed = widths.reduce<number>((n, w) => n + (typeof w === 'number' ? w : 0), 0);
    if (fixed > ctx.layoutWidth) ctx.warnings.push(`${rowLabel} : les colonnes à largeur fixe totalisent ${Math.round(fixed)} px pour un gabarit de ${ctx.layoutWidth} px ; Outlook Windows débordera.`);
  }
  const cells = kept.map((o, i) => colCell(ctx, o, widths[i])).join('');
  return {
    html: `<table ${TABLE} width="100%" style="width:100%;border-collapse:collapse"><tr>${cells}</tr></table>`,
    cols: kept.map((o) => ({ id: o.col.id, blocks: o.blocks })),
  };
}

function layoutHtml(ctx: Ctx, layout: SignatureLayout): { html: string; rendered: RenderedMap } {
  const body = ctx.st.body; // le rôle « texte courant » est le style du gabarit entier
  const w = Math.max(1, Math.round(layout.width));
  ctx.layoutWidth = w;
  layout.rows.forEach((r, i) => ctx.rowIndex.set(r.id, i + 1));
  const rows = layout.rows.map((r) => ({ id: r.id, ...rowHtml(ctx, r, 1) })).filter((r) => r.html);
  return {
    html: `<table ${TABLE} width="${w}" style="width:${w}px;border-collapse:collapse;font-family:${ctx.font};font-size:${Math.round(body.size)}px;line-height:1.4;color:${body.color}">` +
      rows.map((r) => `<tr><td style="padding:0">${r.html}</td></tr>`).join('') +
      `</table>`,
    rendered: { rows: rows.map((r) => ({ id: r.id, cols: r.cols })) },
  };
}

function toText(ctx: Ctx): string {
  const { person: p, brand: b, sendAs } = ctx;
  const v = (f: FieldId) => fieldValue(b, p, sendAs, f);
  return [personName(p, b), v('title'), v('phone_mobile'), v('phone_office'), sendAs, b.address, b.website_url]
    .filter(Boolean)
    .join('\n');
}

export function renderSignature(input: RenderInput, opts: RenderOptions): RenderResult {
  const { person, sendAsEmail, brands, campaigns } = input;
  const now = opts.now ?? new Date();
  const brand = resolveBrand(person, sendAsEmail, brands);
  // no_banner : on ne choisit même pas de campagne (le compte rendu doit refléter « aucune bannière »).
  const campaign = opts.mode === 'gmail'
    ? pickCampaign(campaigns, { brandId: brand.id, emails: [person.primary_email, sendAsEmail], noBanner: person.no_banner }, now)
    : null;
  const ctx: Ctx = {
    person,
    sendAs: sendAsEmail,
    brand,
    campaign,
    campaigns,
    opts,
    warnings: [],
    layoutWidth: 0,
    rowIndex: new Map(),
    st: effectiveStyles(brand),
    font: safeFont(brand.font_family, DEFAULT_FONT),
    base: opts.publicBaseUrl.replace(/\/$/, ''),
  };

  if (person.kind === 'person' && !person.job_title) ctx.warnings.push('Fonction non renseignée.');
  if (person.kind === 'person' && !person.first_name && !person.display_name) {
    ctx.warnings.push('Nom non renseigné : l’adresse mail est affichée à la place.');
  }

  const { html, rendered } = layoutHtml(ctx, effectiveLayout(brand));
  if (html.length > GMAIL_SIGNATURE_MAX_CHARS) {
    ctx.warnings.push(`Signature trop longue pour Gmail : ${html.length} / ${GMAIL_SIGNATURE_MAX_CHARS} caractères.`);
  }
  return { html, text: toText(ctx), brand, campaign, length: html.length, warnings: ctx.warnings, rendered };
}
